import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Badge } from "@/components/ui/badge";
import { Clock, Target, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { NowPlayingSection } from "../components/NowPlayingSection";
import { WeekSelector } from "../components/WeekSelector";
import { H2HInsight } from "../components/H2HInsight";
import HeroHeader from "../components/home/HeroHeader";
import HeroActionBar from "../components/home/HeroActionBar";
import { HeroTopPickCard, HeroTopPickSkeleton } from "../components/home/HeroTopPickCard";
import StatsCards from "../components/home/StatsCards";
import TopNav from "../components/TopNav";
import ShockGamesCarousel from "../components/ShockGamesCarousel";
import BestParlayModal from "../components/BestParlayModal";
import { AddToSlipButton } from "../components/BetSlipDrawer";
import { useBetwayAvailability } from "../hooks/useBetwayAvailability";
import { AliasLearningWidget } from "../components/AliasLearningWidget";
import { countryToFlag } from "../lib/countryFlag";
import LiveOverlay from "../components/LiveOverlay";
import TeamLink from "../components/TeamLink";
import TrustStars from "../components/TrustStars";
import WeekendBigSlate from "../components/WeekendBigSlate";
import HTBTTSAccaCard from "../components/HTBTTSAccaCard";
import FollowTeamButton from "../components/FollowTeamButton";
import GoalsWallTicker from "../components/GoalsWallTicker";
import WhyPanel from "../components/WhyPanel";
import TVBadge from "../components/TVBadge";
import QuantumCompareBadge, { useQuantumComparison } from "../components/QuantumCompareBadge";
import { ChevronDown, HelpCircle } from "lucide-react";

// Iter 82 — Collapsible section toggle.  Persists state in localStorage.
const useCollapsible = (key, defaultExpanded = true) => {
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return defaultExpanded;
    const saved = localStorage.getItem(`collapse_${key}`);
    if (saved === null) return defaultExpanded;
    return saved === "1";
  });
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(`collapse_${key}`, next ? "1" : "0");
      } catch (_) {}
      return next;
    });
  };
  return [open, toggle];
};

const SectionToggle = ({ open, onClick, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
    className="inline-flex items-center justify-center rounded-full border border-border bg-card hover:bg-muted h-8 w-8 transition-all shrink-0"
    data-testid={testId}
  >
    <ChevronDown
      size={16}
      className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
    />
  </button>
);

const HomePage = () => {
  const [openLive, toggleLive] = useCollapsible("now-playing", true);
  const [openWeekend, toggleWeekend] = useCollapsible("weekend-slate", true);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [selectedDay, setSelectedDay] = useState("today");
  const [selectedTier, setSelectedTier] = useState("all");
  const [viewMode, setViewMode] = useState("all");
  const [selectedEliteLeague, setSelectedEliteLeague] = useState("premier-league");
  const [eliteMeta, setEliteMeta] = useState(null);
  const [bigLeaguesStatus, setBigLeaguesStatus] = useState(null);
  const [bigLeaguesMeta, setBigLeaguesMeta] = useState(null);
  const [whyPanel, setWhyPanel] = useState(null); // {fixtureId, marketType} | null
  const [parlayOpen, setParlayOpen] = useState(false);
  const [onlyBetwayReady, setOnlyBetwayReady] = useState(false);
  const [showSettled, setShowSettled] = useState(false);
  const betwayAvailability = useBetwayAvailability(predictions);

  // Inline collapsible Weekend slate so we can use this homepage's
  // auth + toggle state without prop-drilling.
  const CollapsibleWeekendSlate = () => (
    <section className="mt-6 mb-10" aria-labelledby="weekend-slate-heading" data-testid="section-weekend-slate">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 id="weekend-slate-heading" className="font-display text-2xl sm:text-3xl font-semibold tracking-tight inline-flex items-center gap-3">
          This Weekend&apos;s Big Slate
          <span className="eyebrow font-normal">Sat – Sun · 4-5★ picks</span>
        </h2>
        <SectionToggle
          open={openWeekend}
          onClick={toggleWeekend}
          label="Weekend Big Slate"
          testId="toggle-weekend-slate"
        />
      </div>
      {openWeekend && <WeekendBigSlate />}
    </section>
  );
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    if (viewMode === "big-leagues") {
      fetchBigLeaguesPredictions(controller.signal);
    } else if (viewMode === "elite-week") {
      fetchEliteWeekPredictions(selectedEliteLeague, controller.signal);
    } else if (selectedTier === "all") {
      fetchTopPredictions(selectedDay, controller.signal);
    } else {
      fetchTieredPredictions(selectedDay, selectedTier, controller.signal);
    }
    return () => controller.abort();
  }, [selectedDay, viewMode, selectedTier, selectedEliteLeague, showSettled]);

  useEffect(() => { checkBigLeaguesStatus(); }, []);

  const isCanceled = (err) =>
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.message === "canceled";

  const checkBigLeaguesStatus = async () => {
    try {
      const response = await axios.get(`${API}/big-leagues/status`);
      setBigLeaguesStatus(response.data);
    } catch (error) {
      console.error("Error checking big leagues status:", error);
    }
  };

  const fetchTopPredictions = async (day = "today", signal) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ day });
      // Only "today" supports show_settled — tomorrow's matches haven't
      // happened yet so the toggle is a no-op there.
      if (showSettled && day === "today") params.set("show_settled", "true");
      const response = await axios.get(`${API}/predictions/top15?${params}`, { timeout: 120000, signal });
      if (Array.isArray(response.data) && response.data.length > 0) {
        setPredictions(response.data);
      } else {
        const fallbackOrder = ["silver", "gold", "platinum", "diamond", "bronze"];
        let loaded = [];
        for (const t of fallbackOrder) {
          try {
            const r = await axios.get(`${API}/predictions/tiered?day=${day}&tier=${t}`, { signal });
            if (r.data?.predictions?.length > 0) { loaded = r.data.predictions; break; }
          } catch (e) { if (isCanceled(e)) return; }
        }
        setPredictions(loaded);
      }
      setLoading(false);
    } catch (error) {
      if (isCanceled(error)) return;
      console.error("Error fetching predictions:", error);
      toast.error("Failed to load predictions");
      setLoading(false);
    }
  };

  const fetchTieredPredictions = async (day, tier, signal) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/predictions/tiered?day=${day}&tier=${tier}`, { signal });
      setPredictions(response.data.predictions || []);
      setLoading(false);
    } catch (error) {
      if (isCanceled(error)) return;
      if (error.response?.status === 404) {
        toast.error(`${tier} tier not yet compiled. Click "Compile Tiers" to build it.`);
      } else {
        toast.error(`Failed to load ${tier} tier predictions`);
      }
      setPredictions([]);
      setLoading(false);
    }
  };

  const fetchEliteWeekPredictions = async (leagueSlug, signal) => {
    try {
      setLoading(true);
      const r = await axios.get(
        `${API}/elite-leagues/week?league=${encodeURIComponent(leagueSlug)}&days=7`,
        { timeout: 60000, signal }
      );
      setPredictions(r.data.predictions || []);
      setEliteMeta(r.data);
      setLoading(false);
      if ((r.data.predictions || []).length === 0 && r.data.message) {
        toast.info(r.data.message, { duration: 5000 });
      } else {
        const readyText =
          r.data.predictions_ready && r.data.pending
            ? `${r.data.predictions_ready} ready · ${r.data.pending} pending`
            : `${r.data.count} matches`;
        toast.success(
          `${r.data.league?.flag || ""} ${r.data.league?.name || ""} — ${readyText} over 7 days`
        );
      }
    } catch (error) {
      if (isCanceled(error)) return;
      console.error("Error fetching elite week:", error);
      toast.error("Failed to load elite-league predictions");
      setLoading(false);
      setPredictions([]);
    }
  };

  const handleCompileTiers = async () => {
    try {
      setCompiling(true);
      toast.info("Starting tier compilation in the background…");
      const startResp = await axios.post(`${API}/predictions/compile`);
      const jobId = startResp.data.job_id;
      if (!jobId) {
        toast.error("Compilation could not be started");
        setCompiling(false);
        return;
      }
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const status = await axios.get(`${API}/predictions/compile/status?job_id=${jobId}`);
          if (status.data.status === "complete") {
            toast.success("All tiers compiled. Try Diamond / Platinum / Gold / Silver / Bronze.");
            if (selectedTier !== "all") await fetchTieredPredictions(selectedDay, selectedTier);
            setCompiling(false); return;
          }
          if (status.data.status === "failed") {
            toast.error(`Compile failed: ${status.data.error || "unknown error"}`);
            setCompiling(false); return;
          }
        } catch (err) { /* keep polling */ }
      }
      toast.error("Compile timed out after 5 minutes");
    } catch (error) {
      console.error("Compile failed:", error);
      toast.error("Failed to compile tiers");
    } finally { setCompiling(false); }
  };

  const handleExport = (fmt) => {
    const baseUrl = `${API}/exports/predictions?fmt=${fmt}&day=${selectedDay}`;
    const url = selectedTier !== "all" ? `${baseUrl}&tier=${selectedTier}` : baseUrl;
    window.open(url, "_blank");
    toast.success(`Downloading ${fmt.toUpperCase()} export…`);
  };

  const BIG_LEAGUE_KEYWORDS = [
    "Premier League", "La Liga", "Serie A", "Bundesliga", "Ligue 1",
    "Champions League", "Europa League", "Conference League", "UEFA",
    "Nations League", "World Cup", "Africa Cup of Nations", "AFCON",
    "Euro", "Copa America", "Club World Cup",
    "MLS", "Liga MX", "Eredivisie", "Primeira", "CONMEBOL", "Libertadores",
    "Sudamericana", "Championship",
  ];

  const filterBigLeagues = (preds) =>
    (preds || []).filter((p) => {
      const ln = p?.match?.league?.name || "";
      return BIG_LEAGUE_KEYWORDS.some((k) => ln.toLowerCase().includes(k.toLowerCase()));
    });

  const fetchBigLeaguesPredictions = async (signal) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/big-leagues/predictions?day=${selectedDay}`, { timeout: 120000, signal });
      const livePreds = response.data?.predictions || [];
      if (livePreds.length > 0) {
        setPredictions(livePreds);
        setBigLeaguesMeta(response.data);
        if (response.data.european_status?.has_european_elite) {
          toast.success(`🏆 ${response.data.european_elite_count} European elite + ${response.data.global_leagues_count} top global matches!`);
        }
        toast.success(`Loaded ${livePreds.length} premium predictions`);
      } else {
        const fallbackOrder = ["bronze", "silver", "gold", "platinum", "diamond"];
        let collected = []; const seen = new Set();
        for (const t of fallbackOrder) {
          try {
            const r = await axios.get(`${API}/predictions/tiered?day=${selectedDay}&tier=${t}`, { signal });
            for (const p of r.data?.predictions || []) {
              const id = p?.match?.id;
              if (id && !seen.has(id)) { seen.add(id); collected.push(p); }
            }
          } catch (e) { if (isCanceled(e)) return; }
        }
        const big = filterBigLeagues(collected);
        setPredictions(big);
        setBigLeaguesMeta(response.data);
        if (big.length === 0 && response.data?.european_status?.message) {
          toast.info(response.data.european_status.message, { duration: 4000 });
        }
      }
      setLoading(false);
    } catch (error) {
      if (isCanceled(error)) return;
      console.error("Error fetching big leagues predictions:", error);
      toast.error("Big leagues data not available. Showing what we have!");
      setLoading(false); setPredictions([]);
    }
  };

  const handleRefreshPredictions = async () => {
    try {
      setRefreshing(true);
      toast.info("Rebuilding fresh predictions… ~30s");
      // Backend clears the cache + spawns an asyncio.create_task to
      // rebuild. We poll /top15 until a non-empty list is returned
      // (or 90s elapsed).
      await axios.post(`${API}/predictions/refresh`, null, {
        params: { day: selectedDay }, timeout: 15000,
      });

      const start = Date.now();
      let lastData = [];
      while (Date.now() - start < 90000) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const response = await axios.get(`${API}/predictions/top15?day=${selectedDay}`, { timeout: 20000 });
          if (Array.isArray(response.data) && response.data.length > 0) {
            lastData = response.data;
            break;
          }
        } catch (e) {
          // ignore transient poll errors and try again
        }
      }
      if (lastData.length === 0) {
        toast.error("Predictions are taking longer than usual — try again in 30s");
      } else {
        setPredictions(lastData);
        toast.success(`Predictions refreshed — ${lastData.length} matches`);
      }
      setRefreshing(false);
    } catch (error) {
      console.error("Error refreshing predictions:", error);
      toast.error("Failed to refresh predictions");
      setRefreshing(false);
    }
  };

  const formatKickoff = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDateFull = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString([], {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const getRiskTone = (risk) => {
    switch (risk) {
      case "Low": return "bg-success/12 text-success border-success/30";
      case "Medium": return "bg-warning/15 text-warning border-warning/30";
      case "High": return "bg-destructive/12 text-destructive border-destructive/30";
      default: return "bg-muted text-muted-foreground border-border";
    }
  };

  // Iter 145bh — Quantum side-by-side comparison. Build (fixture, market)
  // pairs for every market on every visible prediction so the curated
  // homepage slip lets users see exactly what Quantum would have picked.
  const quantumItems = (predictions || []).flatMap((item) => {
    const fid = item?.match?.fixture_id || item?.match?.id;
    if (!fid) return [];
    return (item?.top_predictions || []).map((pred) => ({
      fixture_id: fid,
      market_type: pred.market_type,
    }));
  });
  const quantumLookup = useQuantumComparison(quantumItems);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav onPlaceParlayClick={() => setParlayOpen(true)} />
      <BestParlayModal open={parlayOpen} onOpenChange={setParlayOpen} predictions={predictions} />

      <ShockGamesCarousel />

      <HeroHeader
        title={
          viewMode === "big-leagues" ? (
            <>Big Leagues. <span className="text-primary">Premium picks.</span></>
          ) : viewMode === "elite-week" && eliteMeta?.league ? (
            <>
              {eliteMeta.league.flag} {eliteMeta.league.name}.
              <br />
              <span className="text-primary">Next 7 days.</span>
            </>
          ) : undefined
        }
        subtitle={
          viewMode === "big-leagues"
            ? "Premium predictions for UEFA Champions League, Premier League, La Liga, Serie A, Bundesliga & Ligue 1."
            : viewMode === "elite-week" && eliteMeta?.league
            ? `Every ${eliteMeta.league.name} fixture in the next week, with data-driven model picks ready the moment a match is compiled.`
            : undefined
        }
        eyebrow={
          viewMode === "big-leagues"
            ? "Top competitions"
            : viewMode === "elite-week"
            ? "Elite week · 7-day outlook"
            : "Top 25 daily picks"
        }
        rightSlot={
          loading && (!predictions || predictions.length === 0)
            ? <HeroTopPickSkeleton />
            : (predictions && predictions.length > 0)
              ? <HeroTopPickCard prediction={predictions[0]} />
              : null
        }
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <HeroActionBar
          viewMode={viewMode} setViewMode={setViewMode}
          bigLeaguesStatus={bigLeaguesStatus} bigLeaguesMeta={bigLeaguesMeta}
          selectedDay={selectedDay} setSelectedDay={setSelectedDay}
          selectedTier={selectedTier} setSelectedTier={setSelectedTier}
          selectedEliteLeague={selectedEliteLeague}
          setSelectedEliteLeague={setSelectedEliteLeague}
          refreshing={refreshing} loading={loading} compiling={compiling}
          onRefresh={handleRefreshPredictions} onCompile={handleCompileTiers}
          onExport={handleExport}
        />

        <StatsCards predictions={predictions} />

        <CollapsibleWeekendSlate />

        <GoalsWallTicker />

        <HTBTTSAccaCard />

        <section className="mb-12" aria-labelledby="now-playing-heading" data-testid="section-now-playing">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 id="now-playing-heading" className="font-display text-2xl sm:text-3xl font-semibold tracking-tight inline-flex items-center gap-3">
              Now Playing
              <span className="eyebrow font-normal">Live · polls every 60s</span>
            </h2>
            <SectionToggle
              open={openLive}
              onClick={toggleLive}
              label="Now Playing"
              testId="toggle-now-playing"
            />
          </div>
          {openLive && <NowPlayingSection />}
        </section>

        <section className="mb-12" aria-labelledby="week-heading">
          <div className="flex items-baseline justify-between mb-4">
            <h2 id="week-heading" className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Week at a glance
            </h2>
            <span className="eyebrow">7 days</span>
          </div>
          <WeekSelector
            selectedDay={selectedDay}
            onSelectDay={(date, label) => {
              if (label === "Today") setSelectedDay("today");
              else if (label === "Tomorrow") setSelectedDay("tomorrow");
              else {
                // Iter 145e — Wed-onwards routes to the Schedule page
                // which has full 7-day prediction coverage.  Previously
                // we showed a toast and stayed on the homepage, leaving
                // the user staring at an empty pick list.
                navigate(`/schedule?d=${date}`);
              }
            }}
          />
        </section>

        {/* Matches list */}
        <section aria-labelledby="predictions-heading">
          <div className="flex items-baseline justify-between mb-4">
            <h2 id="predictions-heading" className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              {viewMode === "big-leagues"
                ? "Premium predictions"
                : viewMode === "elite-week"
                ? `${eliteMeta?.league?.name || ""} — 7-day fixtures`
                : "Today's picks"}
            </h2>
            <span className="eyebrow">
              {viewMode === "elite-week" && eliteMeta
                ? `${eliteMeta.predictions_ready || 0} ready · ${eliteMeta.pending || 0} pending`
                : `${predictions.length} match${predictions.length === 1 ? "" : "es"}`}
            </span>
          </div>

          {/* Toggles row — shown whenever the user is on Today/All/All-tier
              even with 0 predictions, so users can flip "Show finished
              matches today" late in the evening when the live filter
              has already drained the pool. */}
          {selectedDay === "today" && selectedTier === "all" && viewMode === "all" && (
            <div className="mb-3 flex items-center justify-end gap-2 flex-wrap" data-testid="home-toggles">
              <button
                type="button"
                onClick={() => setShowSettled((v) => !v)}
                aria-pressed={showSettled}
                data-testid="show-settled-toggle"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.06em] uppercase border transition-colors
                  ${showSettled
                    ? "bg-foreground text-background border-foreground hover:bg-foreground/90"
                    : "bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"}`}
              >
                <span className={`w-2 h-2 rounded-full ${showSettled ? "bg-emerald-400" : "bg-muted-foreground"}`}></span>
                {showSettled ? "Showing finished + live" : "Show finished matches today"}
              </button>
              {predictions.length > 0 && Object.keys(betwayAvailability).length > 0 && (
                <button
                  type="button"
                  onClick={() => setOnlyBetwayReady((v) => !v)}
                  aria-pressed={onlyBetwayReady}
                  data-testid="betway-ready-filter"
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.06em] uppercase border transition-colors
                    ${onlyBetwayReady
                      ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                      : "bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"}`}
                >
                  <span className={`w-2 h-2 rounded-full ${onlyBetwayReady ? "bg-white" : "bg-emerald-500"}`}></span>
                  {onlyBetwayReady ? "Showing Betway-ready only" : "Betway-ready only"}
                </button>
              )}
            </div>
          )}
          {/* Betway-only toggle for non-Today views (kept simple) */}
          {!(selectedDay === "today" && selectedTier === "all" && viewMode === "all") &&
            predictions.length > 0 &&
            Object.keys(betwayAvailability).length > 0 && (
              <div className="mb-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOnlyBetwayReady((v) => !v)}
                  aria-pressed={onlyBetwayReady}
                  data-testid="betway-ready-filter"
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.06em] uppercase border transition-colors
                    ${onlyBetwayReady
                      ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                      : "bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground"}`}
                >
                  <span className={`w-2 h-2 rounded-full ${onlyBetwayReady ? "bg-white" : "bg-emerald-500"}`}></span>
                  {onlyBetwayReady ? "Showing Betway-ready only" : "Betway-ready only"}
                </button>
              </div>
            )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground"></div>
            </div>
          ) : predictions.length === 0 ? (
            <div
              className="nd-card p-10 text-center"
              data-testid="predictions-empty-state"
            >
              <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-display text-xl font-semibold">No predictions ready yet</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Fixtures haven't synced for {selectedDay} yet. The daily auto-sync
                runs at 00:30 UTC; if this is a fresh deployment, an admin can
                trigger a one-shot sync from{" "}
                <a
                  href="/admin/health"
                  className="text-foreground font-semibold underline underline-offset-2"
                  data-testid="predictions-empty-admin-link"
                >
                  /admin/health
                </a>
                .
              </p>
            </div>
          ) : (
            (() => {
              // Iter 110 — Defensive: drop any prediction missing the
              // `match` envelope (or its required `league` / `home_team`
              // / `away_team` sub-fields).  The Big Leagues fallback
              // path can briefly return raw prediction docs while the
              // backend is mid-rollout.  Without this filter a single
              // malformed entry blows up the whole HomePage with
              // "Cannot read properties of undefined (reading 'league')".
              const safe = (predictions || []).filter(
                (it) =>
                  it &&
                  it.match &&
                  it.match.league &&
                  it.match.home_team &&
                  it.match.away_team,
              );
              const visible = onlyBetwayReady
                ? safe.filter((item) => {
                    const fid = String(item?.match?.fixture_id || item?.match?.id || "");
                    return betwayAvailability[fid] === true;
                  })
                : safe;
              if (onlyBetwayReady && visible.length === 0) {
                return (
                  <div
                    className="nd-card p-10 text-center"
                    data-testid="predictions-empty-betway"
                  >
                    <Target className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <h3 className="font-display text-xl font-semibold">
                      No Betway-ready predictions right now
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                      Betway CA usually opens markets 18–24h before kickoff.
                      Toggle the filter off to see everything, or try a different day.
                    </p>
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  {visible.map((item, index) => {
                    const fid = String(item?.match?.fixture_id || item?.match?.id || "");
                    return (
                <article
                  key={item.match.fixture_id || item.match.id}
                  onClick={() => navigate(`/match/${item.match.fixture_id || item.match.id}`)}
                  className="nd-card nd-card-hover cursor-pointer nd-fade-up p-5 sm:p-6"
                  style={{ animationDelay: `${Math.min(index * 0.04, 0.4)}s` }}
                  data-testid={`match-card-${index}`}
                >
                  {/* Top meta bar */}
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <Badge
                      variant="outline"
                      className="border-border text-muted-foreground bg-transparent text-[11px] font-medium"
                    >
                      <span className="mr-1" aria-hidden>{countryToFlag(item.match.league?.country)}</span>
                      {item.match.league.name}
                    </Badge>
                    {/* Betway availability badge — null means unknown/skip */}
                    {(() => {
                      const avail = betwayAvailability[fid];
                      if (avail === true) {
                        return (
                          <Badge
                            variant="outline"
                            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold tracking-[0.04em]"
                            data-testid={`betway-badge-${fid}`}
                            title="Listed on Betway CA — placement will work."
                          >
                            ✓ ON BETWAY
                          </Badge>
                        );
                      }
                      if (avail === false) {
                        return (
                          <Badge
                            variant="outline"
                            className="border-border bg-muted/60 text-muted-foreground text-[10px] font-bold tracking-[0.04em]"
                            data-testid={`betway-badge-${fid}`}
                            title="Not currently listed on Betway CA. Betway usually opens markets ~18-24h before kickoff."
                          >
                            — NOT ON BETWAY YET
                          </Badge>
                        );
                      }
                      return null;
                    })()}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground font-tabular">
                      <LiveOverlay fixtureId={fid} compact />
                      <TVBadge matchId={fid} compact />
                      {item.settlement && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-slate-500/40 bg-slate-500/10 text-slate-500 text-[10px] font-bold tracking-wider"
                          data-testid={`settled-badge-${fid}`}
                          title={`Final score: ${item.settlement.actual_score?.home}-${item.settlement.actual_score?.away}`}
                        >
                          ● SETTLED · FT {item.settlement.actual_score?.home}-{item.settlement.actual_score?.away}
                          <span className="ml-1 text-emerald-500">✓{item.settlement.markets_won}</span>
                          <span className="text-rose-500">✗{item.settlement.markets_lost}</span>
                        </span>
                      )}
                      <Clock className="w-3.5 h-3.5" />
                      {formatDateFull(item.match.date)}
                      <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
                    </div>
                  </div>

                  {/* Teams row */}
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <img src={item.match.home_team.logo} alt="" className="w-7 h-7 object-contain" />
                      </div>
                      <span className="font-display font-semibold text-base sm:text-lg truncate">
                        <TeamLink name={item.match.home_team.name} onClick={(e) => e.stopPropagation()} />
                      </span>
                      <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                        <FollowTeamButton teamId={item.match.home_team.id} teamName={item.match.home_team.name} size="sm" />
                      </span>
                    </div>

                    <div className="text-[11px] font-bold text-muted-foreground tracking-[0.14em] px-2">
                      VS · {formatKickoff(item.match.date)}
                    </div>

                    <div className="flex items-center gap-3 justify-end min-w-0">
                      <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                        <FollowTeamButton teamId={item.match.away_team.id} teamName={item.match.away_team.name} size="sm" />
                      </span>
                      <span className="font-display font-semibold text-base sm:text-lg truncate text-right">
                        <TeamLink name={item.match.away_team.name} onClick={(e) => e.stopPropagation()} />
                      </span>
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <img src={item.match.away_team.logo} alt="" className="w-7 h-7 object-contain" />
                      </div>
                    </div>
                  </div>

                  {/* H2H (only when present) */}
                  {item.h2h && (
                    <div className="mb-5">
                      <H2HInsight
                        h2h={item.h2h}
                        homeName={item.match.home_team.name}
                        awayName={item.match.away_team.name}
                        compact={false}
                      />
                    </div>
                  )}

                  {/* Markets grid — or a "prediction pending" placeholder */}
                  {(!item.top_predictions || item.top_predictions.length === 0) ? (
                    <div
                      className="rounded-xl border border-dashed border-border bg-subtle px-4 py-5 text-center text-sm text-muted-foreground"
                      data-testid={`pending-pred-${index}`}
                    >
                      Prediction not yet compiled for this fixture. Hit
                      {" "}<span className="text-foreground font-semibold">Compile tiers</span>
                      {" "}on the home page to generate it.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {item.top_predictions.map((pred, idx) => {
                        const verdict = item.settlement?.verdicts?.[pred.market_type];
                        const verdictTone =
                          verdict === "HIT"
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
                            : verdict === "MISS"
                            ? "border-rose-500/40 bg-rose-500/10 text-rose-500"
                            : verdict === "VOID"
                            ? "border-slate-500/40 bg-slate-500/10 text-slate-500"
                            : null;
                        return (
                        <div
                          key={idx}
                          className={`rounded-xl border bg-subtle px-3.5 py-3 transition-colors hover:border-foreground/30 ${
                            verdict === "HIT"
                              ? "border-emerald-500/30"
                              : verdict === "MISS"
                              ? "border-rose-500/30 opacity-80"
                              : "border-border"
                          }`}
                          data-testid={verdict ? `market-${fid}-${idx}-${verdict.toLowerCase()}` : undefined}
                        >
                          <div className="flex items-center justify-between mb-1.5 gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="eyebrow truncate">{pred.market_type}</span>
                              {/* Iter 85c — Engine badge (Q = quantum, C = classic). */}
                              {pred._engine === "quantum" && (
                                <span
                                  className="inline-flex items-center px-1 py-px rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px] font-black tracking-wider"
                                  title="Powered by the Quantum ensemble engine (primary)"
                                  data-testid={`home-market-engine-quantum-${fid}-${idx}`}
                                >
                                  Q
                                </span>
                              )}
                              {pred._engine === "classic" && (
                                <span
                                  className="inline-flex items-center px-1 py-px rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] font-black tracking-wider"
                                  title="Classic engine (Quantum doesn't publish this market)"
                                  data-testid={`home-market-engine-classic-${fid}-${idx}`}
                                >
                                  C
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWhyPanel({
                                    fixtureId: item.match.fixture_id || item.match.id,
                                    marketType: pred.market_type,
                                  });
                                }}
                                aria-label={`Why ${pred.confidence}% confidence`}
                                data-testid={`why-btn-${item.match.fixture_id || item.match.id}-${idx}`}
                                className="p-0.5 rounded-full text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 transition-colors shrink-0"
                              >
                                <HelpCircle className="w-3.5 h-3.5" />
                              </button>
                              <TrustStars
                                score={pred.trust_score}
                                components={pred.trust_components}
                                size="xs"
                                testId={`trust-score-${fid}-${idx}`}
                              />
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {verdictTone && (
                                <span className={`text-[10px] font-bold tracking-[0.08em] uppercase px-1.5 py-0.5 rounded-md border ${verdictTone}`}>
                                  {verdict === "HIT" ? "✓ HIT" : verdict === "MISS" ? "✗ MISS" : "VOID"}
                                </span>
                              )}
                              <span className={`text-[10px] font-bold tracking-[0.06em] uppercase px-1.5 py-0.5 rounded-md border ${getRiskTone(pred.risk_level)}`}>
                                {pred.risk_level}
                              </span>
                              {!verdict && (
                                <AddToSlipButton
                                  pick={{
                                    fixture_id: item.match.fixture_id || item.match.id,
                                    home_team: item.match.home_team.name,
                                    away_team: item.match.away_team.name,
                                    league: item.match.league?.name,
                                    country: item.match.league?.country,
                                    kickoff: item.match.date,
                                    market_type: pred.market_type,
                                    prediction: pred.prediction,
                                    confidence: pred.confidence,
                                    risk_level: pred.risk_level,
                                    odds: pred.odds,
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex items-end justify-between gap-3">
                            <span className="font-display font-semibold text-sm sm:text-base truncate">
                              {pred.prediction}
                            </span>
                            <span className="font-tabular font-bold text-base text-foreground shrink-0">
                              {pred.confidence}%
                            </span>
                          </div>
                          <div className="mt-2 confidence-track">
                            <div className="confidence-fill" style={{ width: `${pred.confidence}%` }} />
                          </div>
                          {/* Iter 85c — Compare-view flip.  Quantum is
                              primary (Iter 85); show the OTHER engine's
                              opinion for transparency.  Uses inline
                              classic_alt / quantum_alt when available
                              (no extra network hop), falls back to the
                              legacy Quantum compare card for classic
                              markets without inline data. */}
                          {pred._engine === "quantum" && pred.classic_alt && (
                            <div
                              className="mt-2 pt-2 border-t border-amber-500/20 flex items-center justify-between gap-2 text-[10px]"
                              data-testid={`home-market-classic-alt-${fid}-${idx}`}
                            >
                              <span className="inline-flex items-center gap-1 text-amber-300 font-black tracking-widest text-[9px]">
                                CLASSIC
                              </span>
                              <span className="text-foreground/80 truncate flex-1">{pred.classic_alt.prediction}</span>
                              <span className="font-tabular text-foreground/80">
                                {pred.classic_alt.confidence != null ? `${Math.round(pred.classic_alt.confidence)}%` : "—"}
                              </span>
                            </div>
                          )}
                          {pred._engine === "classic" && pred.quantum_alt && (
                            <div
                              className="mt-2 pt-2 border-t border-cyan-500/20 flex items-center justify-between gap-2 text-[10px]"
                              data-testid={`home-market-quantum-alt-${fid}-${idx}`}
                            >
                              <span className="inline-flex items-center gap-1 text-cyan-300 font-black tracking-widest text-[9px]">
                                QUANTUM
                              </span>
                              <span className="text-foreground/80 truncate flex-1">{pred.quantum_alt.prediction}</span>
                              <span className="font-tabular text-foreground/80">
                                {pred.quantum_alt.confidence != null ? `${Math.round(pred.quantum_alt.confidence)}%` : "—"}
                              </span>
                            </div>
                          )}
                          {pred._engine === "classic" && !pred.quantum_alt && (
                            <QuantumCompareBadge
                              q={quantumLookup.get(item.match.fixture_id || item.match.id, pred.market_type)}
                              compact
                              testid={`home-market-quantum-${item.match.fixture_id || item.match.id}-${idx}`}
                            />
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </article>
              );})}
            </div>
              );
            })()
          )}
        </section>
      </main>

      <footer className="border-t border-border py-10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} NDAKUBET · Data-driven football intelligence
            </p>
            <p className="text-[11px] text-muted-foreground max-w-xl">
              Predictions are for informational purposes only. Please gamble responsibly.
            </p>
          </div>
        </div>
      </footer>

      <AliasLearningWidget
        predictions={predictions}
        availability={betwayAvailability}
      />

      {whyPanel && (
        <WhyPanel
          fixtureId={whyPanel.fixtureId}
          marketType={whyPanel.marketType}
          onClose={() => setWhyPanel(null)}
        />
      )}
    </div>
  );
};

export default HomePage;
